import type { NavigationLink, HeroContent, FeatureItem, PricingPlan, FAQItem } from '../types';

interface SiteContent {
  navigation: {
    logo: string;
    links: NavigationLink[];
    cta: {
      login: string;
      getStarted: string;
    };
  };
  hero: HeroContent;
  features: {
    headline: string;
    subheadline: string;
    items: FeatureItem[];
  };
  pricing: {
    headline: string;
    subheadline: string;
    plans: PricingPlan[];
  };
  faq: {
    headline: string;
    subheadline: string;
    items: FAQItem[];
  };
  footer: {
    company: {
      name: string;
      description: string;
      email: string;
    };
    links: {
      product: NavigationLink[];
      company: NavigationLink[];
      support: NavigationLink[];
    };
    social: {
      twitter: string;
      linkedin: string;
      github: string;
    };
    legal: {
      copyright: string;
      privacy: string;
      terms: string;
    };
  };
}

export const siteContent: SiteContent = {
  // Site metadata
  meta: {
    title: "Fabrik 28 - AI That Works for Your Business",
    description: "Deploy intelligent workflows in minutes. No technical expertise required. Start automating your WhatsApp, email, and scheduling today.",
    keywords: "AI automation, WhatsApp automation, business workflows, Swiss AI, small business automation",
  },

  // Navigation
  navigation: {
    logo: "Fabrik 28",
    links: [
      { name: "Documentation", href: "/docs" },
    ],
    cta: {
      login: "Login",
      getStarted: "Get Started",
    }
  },

  // Hero Section
  hero: {
    headline: "AI That Works for Your Business, Not Against It",
    subheadline: "Deploy intelligent workflows in minutes. No technical expertise required. Start automating your WhatsApp, email, and scheduling today.",
    cta: {
      primary: "Start Free Trial",
      secondary: "See How It Works",
    },
    trustIndicator: "Trusted by 500+ Swiss businesses",
    customerLogos: [
      { name: "Mueller Construction", logo: "/logos/mueller.svg" },
      { name: "Alpine Retail", logo: "/logos/alpine.svg" },
      { name: "Precision Services", logo: "/logos/precision.svg" },
    ]
  },

  // Problem/Solution Section
  problemSolution: {
    headline: "Small Business Owners Waste 40% of Their Time on Routine Tasks",
    columns: [
      {
        title: "Problem",
        description: "Responding to the same questions repeatedly",
        icon: "ExclamationTriangleIcon"
      },
      {
        title: "Current Solution",
        description: "Expensive consultants or complex DIY tools",
        icon: "CurrencyDollarIcon"
      },
      {
        title: "Fabrik 28 Solution",
        description: "AI workflows that learn your business and handle routine tasks automatically",
        icon: "SparklesIcon"
      }
    ]
  },

  // Core Features
  features: {
    headline: "Everything You Need to Automate Your Business",
    grid: [
      {
        title: "WhatsApp & Email Automation",
        description: "Respond to clients instantly, even when you're on-site",
        icon: "ChatBubbleLeftRightIcon",
        color: "primary"
      },
      {
        title: "Smart Scheduling",
        description: "AI books appointments based on location, availability, and project timelines",
        icon: "CalendarDaysIcon",
        color: "success"
      },
      {
        title: "Project Management",
        description: "Keep projects on track with automated milestone tracking and invoicing",
        icon: "CheckCircleIcon",
        color: "warning"
      },
      {
        title: "Industry Templates",
        description: "Pre-built workflows for construction, retail, and professional services",
        icon: "DocumentDuplicateIcon",
        color: "primary"
      }
    ]
  },

  // Industries
  industries: {
    headline: "Built for Your Industry",
    tabs: [
      {
        id: "construction",
        name: "Construction",
        headline: "From Quote to Completion, Automated",
        workflow: [
          "Client inquiry",
          "AI response",
          "Appointment booking",
          "Project tracking",
          "Invoicing"
        ],
        benefits: [
          "Respond to WhatsApp inquiries in seconds",
          "Automatically schedule site visits based on location",
          "Track project milestones and trigger next steps"
        ],
        testimonial: {
          quote: "Fabrik 28 saves me 15 hours per week on admin tasks",
          author: "Hans Mueller",
          company: "Mueller Construction"
        }
      },
      {
        id: "retail",
        name: "Retail",
        headline: "Customer Service That Never Sleeps",
        workflow: [
          "Customer inquiry",
          "Product recommendations",
          "Order processing",
          "Delivery tracking",
          "Follow-up"
        ],
        benefits: [
          "24/7 customer support automation",
          "Personalized product recommendations",
          "Automated inventory management"
        ],
        testimonial: {
          quote: "Our customer satisfaction scores jumped to 4.8/5",
          author: "Maria Schneider",
          company: "Alpine Retail"
        }
      },
      {
        id: "services",
        name: "Professional Services",
        headline: "Focus on Expertise, Not Administration",
        workflow: [
          "Service inquiry",
          "Consultation booking",
          "Project scoping",
          "Progress updates",
          "Billing"
        ],
        benefits: [
          "Automated consultation scheduling",
          "Client communication workflows",
          "Project milestone tracking"
        ],
        testimonial: {
          quote: "I can focus on growing my business, not managing it",
          author: "Thomas Weber",
          company: "Precision Services"
        }
      }
    ]
  },

  // How It Works
  howItWorks: {
    headline: "Get Started in Under 30 Minutes",
    steps: [
      {
        number: "01",
        title: "Connect Your Tools",
        description: "Link WhatsApp, email, calendar, and CRM",
        icon: "LinkIcon"
      },
      {
        number: "02",
        title: "Choose Your Workflows",
        description: "Select from industry templates or customize your own",
        icon: "Cog6ToothIcon"
      },
      {
        number: "03",
        title: "Start Automating",
        description: "AI learns your business and handles routine tasks",
        icon: "RocketLaunchIcon"
      }
    ],
    cta: "Start Your Free Setup"
  },

  // Customer Success
  customerSuccess: {
    headline: "Real Results from Real Businesses",
    cases: [
      {
        company: "Berner Construction",
        industry: "Construction",
        result: "3x faster client response time",
        quote: "Our WhatsApp bookings increased by 200%",
        metric: "CHF 50K additional revenue in 6 months",
        logo: "/logos/berner.svg"
      },
      {
        company: "Alpine Retail",
        industry: "Retail",
        result: "80% reduction in scheduling conflicts",
        quote: "Customer satisfaction scores jumped to 4.8/5",
        metric: "15 hours saved per week",
        logo: "/logos/alpine.svg"
      },
      {
        company: "Precision Services",
        industry: "Professional Services",
        result: "Automated 90% of routine inquiries",
        quote: "I can focus on growing my business, not managing it",
        metric: "40% increase in client capacity",
        logo: "/logos/precision.svg"
      }
    ]
  },

  // Pricing
  pricing: {
    headline: "Simple Pricing That Grows With You",
    plans: [
      {
        name: "Starter",
        price: "CHF 149",
        period: "month",
        description: "Perfect for small businesses getting started",
        features: [
          "1 workflow active",
          "WhatsApp + Email integration",
          "Basic scheduling",
          "500 AI interactions/month",
          "Email support"
        ],
        cta: "Start Free Trial",
        popular: false
      },
      {
        name: "Professional",
        price: "CHF 299",
        period: "month",
        description: "Most popular for growing businesses",
        features: [
          "5 workflows active",
          "Full integration suite",
          "Advanced scheduling + location optimization",
          "2,000 AI interactions/month",
          "Priority support + onboarding"
        ],
        cta: "Start Free Trial",
        popular: true
      },
      {
        name: "Enterprise",
        price: "Custom",
        period: "pricing",
        description: "For large organizations with specific needs",
        features: [
          "Unlimited workflows",
          "Custom integrations",
          "Dedicated account manager",
          "White-label options",
          "SLA guarantee"
        ],
        cta: "Contact Sales",
        popular: false
      }
    ],
    guarantee: "All plans include: 30-day free trial, No setup fees, Cancel anytime"
  },

  // Technology Trust
  trust: {
    headline: "Swiss-Made AI You Can Trust",
    pillars: [
      {
        title: "Security",
        description: "Bank-grade encryption, GDPR compliant",
        icon: "ShieldCheckIcon"
      },
      {
        title: "Reliability",
        description: "99.9% uptime guarantee",
        icon: "CheckBadgeIcon"
      },
      {
        title: "Local",
        description: "Data stored in Swiss data centers",
        icon: "MapPinIcon"
      },
      {
        title: "Support",
        description: "Native German, French, and English support",
        icon: "ChatBubbleBottomCenterTextIcon"
      }
    ],
    integrations: [
      "WhatsApp Business API",
      "Google Calendar",
      "Microsoft Outlook",
      "Zapier",
      "Slack",
      "Salesforce"
    ]
  },

  // FAQ
  faq: {
    headline: "Questions? We Have Answers",
    questions: [
      {
        question: "How quickly can I get started?",
        answer: "Most businesses are up and running within 30 minutes. Our setup wizard guides you through connecting your tools and choosing your first workflow template."
      },
      {
        question: "Do I need technical knowledge?",
        answer: "Not at all! Fabrik 28 is designed for business owners, not developers. Our visual workflow builder and pre-made templates make it easy for anyone to create powerful automations."
      },
      {
        question: "What integrations do you support?",
        answer: "We integrate with WhatsApp Business API, major email providers, Google Calendar, Outlook, popular CRM systems, and hundreds of other tools through Zapier."
      },
      {
        question: "How does pricing work?",
        answer: "Our pricing is based on the number of active workflows and AI interactions per month. You can start with our free trial and upgrade as your business grows."
      },
      {
        question: "Can I customize workflows?",
        answer: "Absolutely! While we provide industry-specific templates to get you started quickly, you can fully customize workflows to match your unique business processes."
      },
      {
        question: "What kind of support do you provide?",
        answer: "We offer email support for all plans, priority support for Professional plans, and dedicated account management for Enterprise customers. All in German, French, and English."
      }
    ]
  },

  // Final CTA
  finalCta: {
    headline: "Ready to Automate Your Business?",
    subheadline: "Join 500+ Swiss businesses already saving time with Fabrik 28",
    cta: {
      primary: "Start Free Trial",
      secondary: "Schedule Demo"
    },
    guarantee: "30-day free trial • No credit card required • Setup in under 30 minutes"
  },

  // Footer
  footer: {
    company: {
      name: "Fabrik 28",
      // address: "Fabrikstrasse 28, 8854 Siebnen, Switzerland",
      email: "contact@fabrik28.ai",
      // phone: "+41 55 123 45 67"
    },
    links: {
      solutions: [
        { name: "WhatsApp Automation", href: "#" },
        { name: "Email Workflows", href: "#" },
        { name: "Smart Scheduling", href: "#" },
        { name: "Project Management", href: "#" }
      ],
      industries: [
        { name: "Construction", href: "#" },
        { name: "Retail", href: "#" },
        { name: "Professional Services", href: "#" },
        { name: "Healthcare", href: "#" }
      ],
      resources: [
        { name: "Documentation", href: "#" },
        { name: "API Reference", href: "#" },
        { name: "Case Studies", href: "#" },
        { name: "Blog", href: "#" }
      ],
      legal: [
        { name: "Privacy Policy", href: "#" },
        { name: "Terms of Service", href: "#" },
        { name: "Cookie Policy", href: "#" },
        { name: "GDPR", href: "#" }
      ]
    },
    social: [
      { name: "LinkedIn", href: "#", icon: "linkedin" },
      { name: "Twitter", href: "#", icon: "twitter" },
      { name: "GitHub", href: "#", icon: "github" }
    ]
  },

  // Chat Widget
  chat: {
    welcome: "Hi! I'm your Fabrik 28 AI assistant. Ask me anything about automating your business workflows.",
    placeholder: "Type your message...",
    sendButton: "Send",
    closeButton: "Close chat",
    minimizeButton: "Minimize chat",
    title: "Chat with Fabrik 28 AI"
  }
};
